import React, { useMemo } from "react";
import { Tooltip, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ReactComponent as RefreshIcon } from "../assets/RefreshIcon.svg";
import { ReactComponent as ResetFiltersIcon } from "../assets/ResetFiltersIcon.svg";
import ColumnSelectionMenu from "./ColumnSelectionMenu";
import ClearAllIcon from "@material-ui/icons/ClearAll";

// Styles aligned with Material/Google guidelines using theme tokens
const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    width: "100%",
    height: "32px",
    padding: theme.spacing(0, 1),
    minHeight: 44,
    gap: theme.spacing(1),
    marginBottom: 0,
    marginTop: 0,
    backgroundColor: theme.palette.background.default,
  },
  iconButton: {
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.primary.main,
    transition: "box-shadow 200ms ease, transform 150ms ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
      transform: "translateY(-1px)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    "&.Mui-disabled": {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
  icon: {
    width: 32,
    height: 32,
    display: "block",
  },
  resetPrimary: {
    color: theme.palette.primary.main,
  },
  resetDanger: {
    color: theme.palette.error.main,
  },
}));

const TableControls = ({
  resetFiltersFunction,
  clientSidePagination,
  currentFilters,
  handleRefreshClick,
  columnDefs,
  selectedColumns,
  defaultVisibleColumns,
  onColumnSelectionChange,
  GlobalSearch,
  enableRowSelection,
  selectedRows = [],
  bulkActionComponent,
  actionButton,
  onClearSelectedRows = () => {},
}) => {
  const classes = useStyles();

  // Memoized derived booleans
  const showBulk = useMemo(
    () =>
      enableRowSelection && selectedRows.length > 0 && !!bulkActionComponent,
    [enableRowSelection, selectedRows.length, bulkActionComponent]
  );

  // Note: All controls are standardized to 32x32. No server filter-specific UI here.

  if (showBulk) {
    return (
      <div className={classes.container}>
        {bulkActionComponent}
        <Tooltip title="Clear selected rows">
          <IconButton
            color="primary"
            onClick={onClearSelectedRows}
            label="Clear selected rows"
            className={`${classes.iconButton} ${classes.resetDanger}`}
            aria-label="Clear selected rows"
          >
            <ClearAllIcon className={classes.icon} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {resetFiltersFunction && (
        <Tooltip title="Reset Filters">
          <span>
            <IconButton
              color="primary"
              onClick={resetFiltersFunction}
              label="Reset Filters"
              className={`${classes.iconButton} ${classes.resetPrimary}`}
              aria-label="Reset filters"
            >
              <ResetFiltersIcon className={classes.icon} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {handleRefreshClick && (
        <Tooltip title="Refresh Data">
          <IconButton
            color="primary"
            onClick={handleRefreshClick}
            label="Refresh"
            className={`${classes.iconButton} ${classes.resetPrimary}`}
            aria-label="Refresh data"
          >
            <RefreshIcon className={classes.icon} />
          </IconButton>
        </Tooltip>
      )}

      {columnDefs && (
        <ColumnSelectionMenu
          columnDefs={columnDefs}
          selectedColumns={selectedColumns}
          defaultVisibleColumns={defaultVisibleColumns}
          onColumnSelectionChange={onColumnSelectionChange}
          buttonSize={32}
          iconSize={32}
        />
      )}

      {GlobalSearch}

      {actionButton}
    </div>
  );
};

export default React.memo(TableControls);
